import { defineStore } from 'pinia'
import { ref, watch } from 'vue'

import { useLocalization } from '@/shared/composables/localization'
import { getAppURL } from '@/shared/utils/electron'
import { useFetchApi } from '@/shared/utils/fetch'
import { PromiseResolveCallback } from '@/shared/utils/promise'

import { EnvSettingsDto, EnvSettingsDtoValueType, EnvSettingsKey } from './types'
import { parseTypedEnvSettings } from './utils'

export type { EnvSettingsDto, EnvSettingsDtoValueType, EnvSettingsKey } from './types'

export const useStoreEnvSettings = defineStore('envSettings', () => {
  const settings = ref<Array<EnvSettingsDto<EnvSettingsDtoValueType>>>([])
  const _isInitialized = ref(false)
  const _onInitializedPromise = new PromiseResolveCallback<void>()

  const getSettingByKey = <V extends EnvSettingsDtoValueType = string>(key: EnvSettingsKey) => {
    return settings.value.find((s) => s.key === key) as EnvSettingsDto<V>
  }

  const loadAllSettings = (): Promise<EnvSettingsDto<EnvSettingsDtoValueType>[]> => {
    return new Promise((resolve, reject) => {
      if (_isInitialized.value) {
        resolve(settings.value)
        return
      }

      const fetch = useFetchApi()
      fetch
        .get<EnvSettingsDto[]>(getAppURL({ pathName: '/api/env/settings' }))
        .then((response) => {
          if (response.status === 200) {
            if (!Array.isArray(response.data)) {
              reject(new Error('Env settings response is not an array (check backend URL / DOMAINADRESS)'))
              return
            }
            _isInitialized.value = true
            settings.value = parseTypedEnvSettings(response.data)
            resolve(settings.value)
          } else {
            const { t } = useLocalization()
            reject(
              new Error(
                t('common.errorWhileRequest', { err: response.status })),
            )
          }
        })
        .catch((e: unknown) => {
          reject(e)
        })
    })
  }

  watch(_isInitialized, (n, o) => {
    if (n && !o) {
      _onInitializedPromise.resolve()
    }
  })

  return {
    onInitialized: () => _onInitializedPromise.promise,
    getSettingByKey,
    loadAllSettings,
  }
})
